import React from "react";
import { useNavigate } from "react-router-dom";
import UserIdentity from "@/components/UserIdentity";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserPlus, Check, Clock, MoreVertical, Ban, Loader2 } from "lucide-react";

function Action({ user, busy, onAdd, onAccept, onUnblock }) {
  if (busy) return <Loader2 className="h-4 w-4 animate-spin text-pink-500" />;
  switch (user.relationship) {
    case "friends":
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-600"
          data-testid={`state-friends-${user.username}`}
        >
          <Check className="h-3.5 w-3.5" /> Friends
        </span>
      );
    case "request_sent":
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500"
          data-testid={`state-requested-${user.username}`}
        >
          <Clock className="h-3.5 w-3.5" /> Requested
        </span>
      );
    case "request_received":
      return (
        <Button
          size="sm"
          onClick={() => onAccept(user)}
          data-testid={`btn-accept-${user.username}`}
          className="gap-1 rounded-full bg-pink-500 font-semibold text-white transition-transform hover:bg-pink-600 active:scale-95"
        >
          <Check className="h-4 w-4" /> Accept
        </Button>
      );
    case "blocked_by_me":
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onUnblock(user)}
          data-testid={`btn-unblock-${user.username}`}
          className="rounded-full font-semibold"
        >
          Unblock
        </Button>
      );
    default:
      return (
        <Button
          size="sm"
          onClick={() => onAdd(user)}
          data-testid={`btn-add-friend-${user.username}`}
          className="gap-1 rounded-full bg-pink-500 font-semibold text-white transition-transform hover:bg-pink-600 active:scale-95"
        >
          <UserPlus className="h-4 w-4" /> Add
        </Button>
      );
  }
}

export default function UserRow({ user, busy, onAdd, onAccept, onBlock, onUnblock }) {
  const navigate = useNavigate();
  return (
    <div
      className="flex items-center justify-between gap-2 p-3 transition-colors hover:bg-pink-50/40 sm:gap-3 sm:p-4"
      data-testid={`user-row-${user.username}`}
    >
      <button
        onClick={() => navigate(`/u/${user.username}`)}
        className="min-w-0 flex-1 text-left"
        data-testid={`open-profile-${user.username}`}
      >
        <UserIdentity user={user} />
      </button>
      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        <Action user={user} busy={busy} onAdd={onAdd} onAccept={onAccept} onUnblock={onUnblock} />
        {user.relationship !== "blocked_by_me" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100"
                data-testid={`menu-${user.username}`}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuItem
                className="text-red-500 focus:text-red-500"
                onClick={() => onBlock(user)}
                data-testid={`menu-block-${user.username}`}
              >
                <Ban className="mr-2 h-4 w-4" /> Block user
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
